import { Box, Card, CardContent, Divider, Typography } from '@mui/material';

import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import { AccountLocation } from '../../../../../types/account';
import { formatDateTime } from '../../../../../utils/dateTime';

type AccountLocationNameDescriptionVariant = 'banner' | 'card' | 'both';

interface AccountLocationNameDescriptionProps {
  location: AccountLocation;
  variant?: AccountLocationNameDescriptionVariant;
  actionButtons?: React.ReactNode;
}

const formatAddress = (location: AccountLocation): string[] => {
  const lines = [location.address_line_1, location.address_line_2].filter(Boolean) as string[];
  const cityStateZip = [location.address_city, location.address_state, location.address_zip].filter(Boolean).join(', ');

  if (cityStateZip) {
    lines.push(cityStateZip);
  }

  return lines;
};

export default function AccountLocationNameDescription({
  location,
  variant = 'banner',
  actionButtons,
}: AccountLocationNameDescriptionProps): JSX.Element {
  const { theme } = useThemeContext();
  const hasDescription = Boolean(location.description?.trim());
  const description = hasDescription ? location.description : 'No description provided';
  const showBanner = variant === 'banner' || variant === 'both';
  const showCard = variant === 'card' || variant === 'both';
  const contentGap = showBanner && showCard ? '16px' : 0;
  const addressLines = formatAddress(location);
  const createdAt = formatDateTime(location.created_at);
  const updatedAt = formatDateTime(location.modified_at);

  return (
    <Box sx={{ display: 'grid', gap: contentGap }}>
      {showBanner ? (
        <Box
          sx={{
            backgroundColor: theme.layout.defaults.backgroundColor,
            borderRadius: '16px',
            padding: '12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <Typography
              component="span"
              variant="h2"
              sx={{
                color: theme.custom.components.headerBanner.title.color,
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '28px',
                lineHeight: '32px',
                letterSpacing: '-0.25px',
              }}
            >
              {location.name}
            </Typography>
            <Typography component="span" variant="body1" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {showCard ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                  Location Details
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Name: {location.name}
                </Typography>

                {hasDescription ? (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Description: {location.description}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No description provided.
                  </Typography>
                )}
              </Box>
              {actionButtons && <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>{actionButtons}</Box>}
            </Box>

            {addressLines.length ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Address
                </Typography>
                {addressLines.map((line) => (
                  <Typography key={line} variant="body2" color="text.secondary">
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : null}

            <Divider sx={{ my: 2 }} />

            {createdAt ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {createdAt}
              </Typography>
            ) : null}
            {updatedAt ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Updated: {updatedAt}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
}
